import { Box, Target, Text } from "@modernatx-internal/component-library";
import { usePathname } from "next/navigation";
import React from "react";

import { ContentComponentProps, PageHeaderLinkContent } from "@/content-V2/types";
import { useParseLocalizedPathname } from "@/hooks/useParseLocalizedPathname";

import { useIsMobile } from "../hooks/useIsMobile";
import { PageHeaderScrollableContext } from "./PageHeader";
import { usePageHeaderSubnav } from "./PageHeaderSubnav";

export const PageHeaderLink: React.FC<ContentComponentProps<PageHeaderLinkContent>> = ({
  children,
  disabled,
  href,
  links
}) => {
  const isMobile = useIsMobile();
  const { setPageHeaderSubnav, target: subnavTarget } = usePageHeaderSubnav();
  const target = React.useRef<HTMLElement>(null);
  const { scrollTo } = React.useContext(PageHeaderScrollableContext);
  const { getLocalizedPathnamesMatch } = useParseLocalizedPathname();
  const pathname = usePathname();
  const subnavVisible = subnavTarget !== null && subnavTarget === target.current;
  const hasActiveSubLink = React.useMemo(() => {
    let hasActiveSubLinkResult = false;

    React.Children.forEach(links, (link) => {
      if (React.isValidElement(link) && getLocalizedPathnamesMatch(pathname, link.props.href)) {
        hasActiveSubLinkResult = true;
      }
    });

    return hasActiveSubLinkResult;
  }, [getLocalizedPathnamesMatch, pathname, links]);
  const active =
    !disabled && (hasActiveSubLink || (href ? getLocalizedPathnamesMatch(pathname, href) : false));

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      if (isMobile) {
        // Do nothing...
      } else if (links && links.length) {
        if (!subnavVisible) {
          e.preventDefault();

          setPageHeaderSubnav({
            links,
            target: target.current
          });
        }
      } else {
        setPageHeaderSubnav({
          links: null,
          target: null
        });
      }
    },
    [isMobile, links, setPageHeaderSubnav, subnavVisible]
  );

  const handleMouseEnter = React.useCallback(() => {
    if (!isMobile) {
      setPageHeaderSubnav({
        links: links || null,
        target: links ? target.current : null
      });
    }
  }, [isMobile, links, setPageHeaderSubnav]);

  React.useEffect(() => {
    if ((active || subnavVisible) && target.current) {
      scrollTo(target.current);
    }
  }, [active, subnavVisible, scrollTo]);

  return (
    <>
      <Target
        ref={target}
        disabled={disabled}
        href={href}
        sx={{
          color: disabled
            ? "tab-text-disabled"
            : active
              ? "tab-stroke-selected"
              : "tab-text-default",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 3,
          transition: "transition-color-medium",
          ":active": {
            color: disabled ? "tab-text-disabled" : "tab-stroke-click"
          },
          ":hover": {
            color: disabled ? "tab-text-disabled" : "tab-stroke-selected"
          },
          "&:first-of-type": {
            paddingInlineStart: 0
          },
          br: {
            display: ["none", null, "initial"],
            // @ts-ignore
            "@media screen and (min-width: 1280px)": {
              display: "none"
            }
          }
        }}
        onClick={disabled ? undefined : handleClick}
        onMouseEnter={disabled ? undefined : handleMouseEnter}
      >
        <Text
          variant="title-1-medium"
          sx={{
            color: "inherit",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
            minWidth: 0,
            overflowWrap: "break-word",
            px: 1,
            py: 2,
            textAlign: "center",
            whiteSpace: ["nowrap", null, "normal"],
            wordBreak: "normal",
            span: {
              textWrap: ["unset", null, "pretty"],
              overflowWrap: [null, null, "normal"],
              wordBreak: [null, null, "keep-all"],
              whiteSpace: [null, null, "normal"]
            }
          }}
        >
          {children}
        </Text>
        <Box
          as="span"
          data-underline="1"
          sx={{
            backgroundColor: active ? "tab-stroke-selected" : "transparent",
            display: "block",
            height: () => "3px",
            transform: `translateY(${active ? 0 : "3px"})`,
            transition: "transition-ease-out-fast"
          }}
        />
      </Target>
    </>
  );
};
